import { useCallback, useMemo } from "react";

import { ApiError } from "@steto-care/steto-query";
import { createStore } from "zustand/vanilla";

import { apiDataSource } from "@/io/datasource/ApiDatasource.ts";
import { LoginIdentifier } from "@/io/repository/AuthRepository.ts";
import { Queries } from "@/queries/Queries.ts";
import { Sentry } from "@/utils/sentryUtils.ts";
import { makeZustandContextStore } from "@/utils/zustandUtils";

export type NoToken = undefined | null | false;
export type Token = string | NoToken;

export type AuthData =
  | {
      status: "pending";
      accessToken?: NoToken;
    }
  | {
      status: "unauthenticated";
      accessToken?: NoToken;
    }
  | {
      status: "authenticated";
      accessToken: string;
    };

export type AuthState = AuthData & {
  setPending: () => void;
  setUnauthenticated: () => void;
  setAuthenticated: (token: string) => void;
};

const createAuthStore = (initProps: AuthData) => {
  return createStore<AuthState>()(set => ({
    ...initProps,
    setPending: () => set({ status: "pending", accessToken: undefined }),
    setUnauthenticated: () => set({ status: "unauthenticated", accessToken: undefined }),
    setAuthenticated: (token: string) => set({ status: "authenticated", accessToken: token }),
  }));
};

const [, AuthProvider, useAuthState] = makeZustandContextStore<AuthData, AuthState>("Auth", createAuthStore);

const authenticationAccessor = (state: AuthState) => ({
  setAuthenticated: state.setAuthenticated,
  setUnauthenticated: state.setUnauthenticated,
});

const useAuthenticate = () => {
  const refreshTokens = Queries.auth.useRefreshTokens();
  const auth = useAuthState(authenticationAccessor);

  const callback = useCallback(
    (token: Token) => {
      if (!token) {
        auth.setUnauthenticated();
        apiDataSource.destroyInstance();
        return;
      } else {
        auth.setAuthenticated(token);
        apiDataSource.createInstance(token);
        apiDataSource.onUnauthorized(() =>
          refreshTokens
            .mutateAsync({})
            .then(callback)
            .catch(() => callback(false)),
        );
      }
    },
    [auth, refreshTokens],
  );

  return callback;
};

const useRestoreSession = () => {
  const refreshTokens = Queries.auth.useRefreshTokens();
  const authenticate = useAuthenticate();

  return useCallback(async () => {
    try {
      authenticate(await refreshTokens.mutateAsync({}));
    } catch (error) {
      // A 4xx here means no session, or an expired / blacklisted refresh token:
      // expected on any cold start, not an incident. BaseApiDatasource already
      // reports everything unexpected, so only server errors are worth a capture.
      if (!(error instanceof ApiError) || error.data.statusCode >= 500) {
        Sentry.captureException(error);
      }
      // Never leave the app on 'pending': that hangs it on the loading screen.
      authenticate(false);
    }
  }, [refreshTokens, authenticate]);
};

const useRequestLoginCode = () => {
  const requestCode = Queries.auth.useRequestLoginCode();

  const request = useCallback((identifier: LoginIdentifier) => requestCode.mutateAsync(identifier), [requestCode]);

  return useMemo(
    () => ({
      request,
      pending: requestCode.isPending,
      error: requestCode.error,
    }),
    [request, requestCode.isPending, requestCode.error],
  );
};

const useLoginWithCode = () => {
  const verifyCode = Queries.auth.useVerifyLoginCode();
  const authenticate = useAuthenticate();

  const login = useCallback(
    (code: string, identifier: LoginIdentifier) => verifyCode.mutateAsync({ code, identifier }).then(authenticate),
    [verifyCode, authenticate],
  );

  return useMemo(
    () => ({ login, pending: verifyCode.isPending, error: verifyCode.error }),
    [login, verifyCode.isPending, verifyCode.error],
  );
};

const useLogout = () => {
  const logout = Queries.auth.useLogout();
  const authenticate = useAuthenticate();

  return useCallback(async () => {
    // Revoking is best effort: an offline or failing api must not trap the user
    // in a session they asked to leave. The token is dropped locally regardless.
    await logout.mutateAsync({}).catch(Sentry.captureException);
    authenticate(false);
  }, [logout, authenticate]);
};

const dataAccessor = (state: AuthState) => {
  const { setAuthenticated, setPending, setUnauthenticated, ...data } = state;
  return data;
};

const useAuth = () => {
  const logout = useLogout();
  const restoreSession = useRestoreSession();
  const authState = useAuthState(dataAccessor);
  return useMemo(
    () => ({
      logout,
      restoreSession,
      ...authState,
    }),
    [logout, restoreSession, authState],
  );
};

export { useAuth, AuthProvider, useLoginWithCode, useRequestLoginCode };
